import { PropertySelections } from './types';
import {
    applyAttachmentSelections,
    ParticleTreeNode,
    ResolveFurnitureChoiceTree,
} from './furniture';
import * as Hooks from 'preact/hooks';
import { useCachingResolveFurnitureChoiceTree } from './caching';
import { useAsync } from '../hooks/useAsync';
import { AnyAction, Dispatch } from 'redux';

export function useAssembleFurniture(
    modelsUrl: string | undefined,
    spaceId: string | undefined,
    propertySelections: PropertySelections | undefined,
    attachmentProposals: Record<string, string>,
    dispatch: Dispatch<AnyAction>,
    disableErrorHandling: boolean,
): {
    furnitureTree?: ParticleTreeNode;
    attachmentSelections: Record<string, string>;
    resolveFurnitureChoiceTree: ResolveFurnitureChoiceTree;
} {
    const { resolveFurnitureChoiceTree } = useCachingResolveFurnitureChoiceTree(
        spaceId,
        modelsUrl,
        dispatch,
        disableErrorHandling,
    );

    const furnitureChoices = useAsync(
        async () =>
            modelsUrl && spaceId && propertySelections
                ? await resolveFurnitureChoiceTree(propertySelections)
                : undefined,
        [spaceId, propertySelections, modelsUrl, resolveFurnitureChoiceTree],
    ).result;

    const assembledFurniture = Hooks.useMemo(
        () =>
            furnitureChoices
                ? applyAttachmentSelections(
                      furnitureChoices.attachmentOptions,
                      attachmentProposals,
                      furnitureChoices.furnitureChoiceTree,
                  )
                : undefined,
        [
            furnitureChoices?.attachmentOptions,
            attachmentProposals,
            furnitureChoices?.furnitureChoiceTree,
        ],
    );

    const furnitureTree = assembledFurniture?.furnitureTree ?? undefined;
    const attachmentSelections = assembledFurniture?.attachmentSelections ?? {};

    return { furnitureTree, attachmentSelections, resolveFurnitureChoiceTree };
}
